import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

export const LANGUAGE_STORAGE_KEY = 'ukiyo-language';

const CITY_AR_MAP = {
    "Cairo": "القاهرة",
    "Alexandria": "الإسكندرية",
    "Giza": "الجيزة",
    "Shubra El-Kheima": "شبرا الخيمة",
    "Port Said": "بورسعيد",
    "Suez": "السويس",
    "Luxor": "الأقصر",
    "al-Mansura": "المنصورة",
    "El-Mahalla El-Kubra": "المحلة الكبرى",
    "Tanta": "طنطا",
    "Asyut": "أسيوط",
    "Ismailia": "الإسماعيلية",
    "Fayyum": "الفيوم",
    "Zagazig": "الزقازيق",
    "Aswan": "أسوان",
    "Damietta": "دمياط",
    "Damanhur": "دمنهور",
    "al-Minya": "المنيا",
    "Beni Suef": "بني سويف",
    "Qena": "قنا",
    "Sohag": "سوهاج",
    "Hurghada": "الغردقة",
    "6th of October City": "مدينة 6 أكتوبر",
    "Shibin El Kom": "شبين الكوم",
    "Banha": "بنها",
    "Kafr el-Sheikh": "كفر الشيخ",
    "Arish": "العريش",
    "Belbeis": "بلبيس",
    "Marsa Matruh": "مرسى مطروح",
    "Kafr El Dawwar": "كفر الدوار",
    "Qalyub": "قليوب",
    "Abu Kabir": "أبو كبير"
};

const CATEGORY_AR_MAP = {
    "vases": "فازات",
    "vase": "فازات",
    "tables": "طاولات",
    "table": "طاولات",
    "coffee tables": "طاولات قهوة",
    "side tables": "طاولات جانبية",
    "consoles": "كونسول",
    "lighting": "إضاءة",
    "lamps": "مصابيح وإضاءة",
    "mirrors": "مرايا",
    "paintings": "لوحات فنية",
    "wall art": "لوحات وديكور حائط",
    "accessories": "إكسسوارات منزلية",
    "home accessories": "إكسسوارات منزلية",
    "candles": "شموع وفواحات",
    "trays": "صواني ديكور",
    "sculptures": "منحوتات وتحف",
    "bowls": "أطباق ديكور",
    "planters": "أحواض نباتات",
    "cushions": "وسائد ديكور",
    "rugs": "سجاد"
};

const translations = {
    en: {
        // Header & SubHeader
        menu: "Menu",
        search: "Search",
        cart: "Cart",
        products: "Products",
        allProducts: "All Products",
        onSale: "On Sale",
        categories: "Categories",
        switchLangLabel: "العربية",
        switchLangShort: "AR",

        // MenuPanel
        loginRegister: "Login / Register",
        home: "Home",
        viewAll: "View All",
        myProfile: "My Profile",
        myOrders: "My Orders",
        myWishlist: "My Wishlist",
        adminPanel: "Admin Panel",
        logout: "Logout",
        language: "Language",

        // LandingPage
        shopByCategory: "Shop by Category",
        newArrivals: "New Arrivals",
        viewAllCaps: "VIEW ALL",
        soldOut: "Sold Out",
        liveInTheMoment: "Live in the Moment",
        brandPhilosophyQuote: '"Curating serenity for the modern Egyptian home. Where minimalism meets warmth, and every object tells a quiet story."',
        exploreCollection: "EXPLORE COLLECTION",

        // ShopPage & FilterPanel
        searchResults: "Search Results",
        filters: "Filters",
        sortBy: "Sort by",
        priceLowToHigh: "Price: Low to High",
        priceHighToLow: "Price: High to Low",
        highestRating: "Highest Rating",
        loadMore: "Load More",
        showingProducts: "Showing {visible} of {total} products",
        noProductsMatch: "No products match the current filters.",
        maxPriceLabel: "Max Price: EGP {price}",
        stockStatus: "Stock Status",
        all: "All",
        inStock: "In Stock",
        outOfStock: "Out of Stock",
        onSaleOnly: "On Sale Only",
        category: "Category",
        clear: "Clear",
        apply: "Apply",
        onSaleSubtitle: "Discover our best deals and special offers.",
        noSaleItems: "There are currently no items on sale.",

        // ProductCard & ProductDetailView
        saleBadge: "SALE",
        noDescription: "No description available.",
        addToCart: "Add to Cart",
        shippingAccordionTitle: "Shipping & Protective Packing",
        shippingBullet1Strong: "Protective Multi-Layer Wrapping:",
        shippingBullet1Text: "Every piece is wrapped in specialized protective packaging to prevent transit damage. For this reason, opening or inspecting the package prior to receiving it from the courier is not permitted.",
        shippingBullet2Strong: "Pre-Shipment Photo & Video (Upon Request):",
        shippingBullet2Text: "Photos and videos of your product while it is being packed can be provided upon customer request prior to dispatch for complete peace of mind.",
        shippingBullet3Strong: "Estimated Delivery:",
        shippingBullet3Text: "1–3 business days in Alexandria, 3–5 business days in Cairo & Giza, and 4–7 business days for other governorates.",
        returnsAccordionTitle: "Returns & Unboxing Video Policy",
        returnsBullet1Strong: "Record an Unboxing Video:",
        returnsBullet1Text: "Please record a clear video while unboxing your sealed order. In case of transit damage, this video is required evidence that the courier is responsible.",
        returnsBullet2Strong: "14-Day Return Window:",
        returnsBullet2Text: "Returns and exchanges are accepted within 14 days for items in their original unused condition and packaging.",
        returnsBullet3Strong: "Non-Returnable Items:",
        returnsBullet3Text: "Paintings, special/custom orders, and items not in their original condition cannot be returned or exchanged.",
        viewFullPolicies: "View Full Policies & Terms →",
        customerReviews: "Customer Reviews",
        noReviewsYet: "No reviews yet. Be the first to review this product!",
        writeReview: "Write a Review",
        ratingLabel: "Rating",
        reviewLabel: "Review",
        reviewPlaceholder: "Share your thoughts about this product...",
        submitting: "Submitting...",
        submitReview: "Submit Review",
        verifiedBuyerNotice: "Only verified buyers who have purchased and received this product can leave a review.",
        loginToReview: "Please log in to write a review.",

        // SearchOverlay
        searchPlaceholder: "Search for products...",
        noSearchResults: 'No results found for "{query}"',

        // Cart
        shoppingCart: "Shopping Cart",
        cartEmpty: "Your cart is empty.",
        continueShopping: "Continue Shopping",
        remove: "Remove",
        subtotal: "Subtotal",
        shippingCalculatedAtCheckout: "Shipping calculated at checkout.",
        proceedToCheckout: "Proceed to Checkout",

        // CheckoutModal
        checkoutTitle: "Checkout",
        shippingInfoHeading: "1. Shipping Information",
        fullNamePlaceholder: "Full Name *",
        phonePlaceholder: "Phone Number *",
        emailInvoicePlaceholder: "Email Address (for Order ID & Invoice) *",
        selectCityPlaceholder: "Select City *",
        streetAddressPlaceholder: "Detailed Street Address *",
        couponPlaceholder: "Coupon Code",
        iHaveReadAndAgree: "I have read and agree to the",
        termsAndConditions: "Terms & Conditions",
        returnAndExchangePolicy: "Return & Exchange Policy",
        noCourierInspectionNote: "(including no open-package courier inspection & unboxing video requirement)",
        andText: "and",
        buyingAndShippingPolicy: "Buying & Shipping Policy",
        paymentMethodHeading: "2. Payment Method",
        creditDebitCard: "Credit / Debit Card",
        paySecurelyVisaMastercard: "Pay securely with Visa, Mastercard, Amex, or Meeza",
        cashOnDelivery: "Cash on Delivery (COD)",
        payCashUponReceiving: "Pay with cash upon receiving your order",
        loadingCardFields: "Loading secure card fields...",
        fillShippingFirstTitle: "Please fill in your Shipping Information above first",
        fillShippingFirstSub: "Once your name, phone, email, city, and address are entered, the card form below will unlock automatically.",
        agreeTermsForCardTitle: "Please agree to our Store Policies & Terms to proceed with card payment",
        iAgreeToTermsBtn: "I Agree to Terms & Conditions",
        shippingLabel: "Shipping",
        freeLabel: "FREE",
        paymentFeeLabel: "Payment Fee",
        discountLabel: "Discount",
        totalLabel: "Total",
        checkTermsBeforeOrderWarning: "Please check the box above to agree to our Terms & Conditions before placing your order.",
        placeOrderCodBtn: "Place Order (Cash on Delivery)",
        waitSignUpSave: "Wait! Sign Up & Save?",
        createAccountPromo: "Create an account now to get exclusive offers and track your orders easily.",
        signUpLoginBtn: "Sign Up / Login",
        orContinueAsGuest: "Or continue as guest:",
        enterEmailForInvoice: "Enter your email for invoice",
        continueAsGuestBtn: "Continue as Guest",

        // OrderConfirmationModal
        orderPlacedTitle: "Order Placed!",
        thankYouForShopping: "Thank you for shopping with Ukiyo.",
        checkEmailForInvoice: "Please check your email for your Order ID and detailed invoice.",

        // Footer
        quickLinks: "Quick Links",
        findUsHere: "Find Us Here",
        storeAddress: "19 Farid Bek st. Kafr Abdo, Alexandria.",
        whatsappLabel: "WhatsApp",
        weAccept: "We Accept",
        allRightsReserved: "Ukiyo. All rights reserved.",

        // Policies & Consent Banner / Modal / Page
        storePoliciesBannerTitle: "Ukiyo Store Policies:",
        pleaseReviewAndAccept: "Please review and accept our",
        decline: "Decline",
        acceptAll: "Accept All",
        storePoliciesAndTerms: "Store Policies & Terms",
        policiesAndTermsTitle: "Policies & Terms",
        policiesPageSubtitle: "Select a section below to review our Return & Exchange Policy, Buying & Shipping Policy, and Terms & Conditions.",
        backToHome: "Back to Home",
        close: "Close",
        iAcceptAndAgree: "I Accept & Agree",

        // Auth, Profile, Orders, Wishlist
        createAccount: "Create Account",
        loginToUkiyo: "Login to Ukiyo",
        fullName: "Full Name",
        email: "Email",
        password: "Password",
        register: "Register",
        login: "Login",
        orDivider: "OR",
        signInWithGoogle: "Sign in with Google",
        alreadyHaveAccount: "Already have an account? Login",
        dontHaveAccount: "Don't have an account? Register",
        useSignInLink: "Use sign-in link",
        signInWithEmailLink: "Sign in with Email Link",
        enterEmailForLink: "Enter your email to receive a password-free sign-in link.",
        sendLink: "Send Link",
        backToPasswordLogin: "Back to password login",
        checkYourEmail: "Check your Email",
        manageShippingAndAccount: "Manage your shipping and account details.",
        city: "City",
        selectACity: "Select a city",
        detailedAddress: "Detailed Address",
        saveChanges: "Save Changes",
        accountSettings: "Account Settings",
        emailAddress: "Email Address",
        cancel: "Cancel",
        changeEmail: "Change Email",
        newEmail: "New Email",
        currentPasswordVerify: "Current Password (for verification)",
        updateEmail: "Update Email",
        resetPassword: "Reset Password",
        loadingOrders: "Loading your orders...",
        pleaseLoginOrders: "Please log in to see your orders.",
        orderNumber: "Order #{id}",
        placedOn: "Placed on:",
        justNow: "Just now",
        noOrdersYet: "You have not placed any orders yet.",
        wishlistEmpty: "Your wishlist is empty.",
        startExploring: "Start exploring"
    },
    ar: {
        // Header & SubHeader
        menu: "القائمة",
        search: "بحث",
        cart: "السلة",
        products: "المنتجات",
        allProducts: "جميع المنتجات",
        onSale: "العروض والخصومات",
        categories: "الأقسام",
        switchLangLabel: "English",
        switchLangShort: "EN",

        // MenuPanel
        loginRegister: "تسجيل الدخول / إنشاء حساب",
        home: "الرئيسية",
        viewAll: "عرض الكل",
        myProfile: "حسابي",
        myOrders: "طلباتي",
        myWishlist: "المفضلة",
        adminPanel: "لوحة التحكم",
        logout: "تسجيل الخروج",
        language: "اللغة",

        // LandingPage
        shopByCategory: "تسوق حسب القسم",
        newArrivals: "وصل حديثاً",
        viewAllCaps: "عرض الكل",
        soldOut: "نفد من المخزون",
        liveInTheMoment: "عِش جمال اللحظة",
        brandPhilosophyQuote: '"نصنع السكينة والجمال للمنزل المصري العصري؛ حيث تلتقي البساطة بالدفء، وتحكي كل قطعة قصة هادئة."',
        exploreCollection: "استكشف المجموعة",

        // ShopPage & FilterPanel
        searchResults: "نتائج البحث",
        filters: "تصفية",
        sortBy: "ترتيب حسب",
        priceLowToHigh: "السعر: من الأقل للأعلى",
        priceHighToLow: "السعر: من الأعلى للأقل",
        highestRating: "الأعلى تقييماً",
        loadMore: "عرض المزيد",
        showingProducts: "عرض {visible} من أصل {total} منتج",
        noProductsMatch: "لا توجد منتجات تطابق اختيارات التصفية الحالية.",
        maxPriceLabel: "الحد الأقصى للسعر: EGP {price}",
        stockStatus: "حالة التوفر",
        all: "الكل",
        inStock: "متوفر في المخزون",
        outOfStock: "غير متوفر",
        onSaleOnly: "العروض فقط",
        category: "القسم",
        clear: "إعادة ضبط",
        apply: "تطبيق",
        onSaleSubtitle: "اكتشف أفضل العروض والخصومات الحصرية لدينا.",
        noSaleItems: "لا توجد منتجات في العروض حالياً.",

        // ProductCard & ProductDetailView
        saleBadge: "خصم",
        noDescription: "لا يوجد وصف متاح لهذا المنتج.",
        addToCart: "أضف إلى السلة",
        shippingAccordionTitle: "الشحن والتغليف الوقائي",
        shippingBullet1Strong: "تغليف وقائي متعدد الطبقات:",
        shippingBullet1Text: "يتم تغليف كل قطعة بعناية فائقة بطبقات حماية خاصة لمنع أي تلف أثناء الشحن. ولهذا السبب، لا يُسمح بفتح الشحنة أو معاينتها قبل استلامها من مندوب الشحن.",
        shippingBullet2Strong: "توثيق بالصور والفيديو قبل الشحن (عند الطلب):",
        shippingBullet2Text: "يمكننا تصوير وتوثيق طلبك (صور وفيديو) أثناء التغليف بناءً على طلب العميل المسبق قبل خروج الشحنة لضمان راحة بالك.",
        shippingBullet3Strong: "مدة التوصيل المتوقعة:",
        shippingBullet3Text: "من 1 إلى 3 أيام عمل في الإسكندرية، ومن 3 إلى 5 أيام عمل في القاهرة والجيزة، ومن 4 إلى 7 أيام عمل لباقي المحافظات.",
        returnsAccordionTitle: "سياسة الاسترجاع وفيديو فتح الشحنة",
        returnsBullet1Strong: "نصيحة هامة — صوّر فيديو أثناء فتح الشحنة:",
        returnsBullet1Text: "يرجى تصوير فيديو واضح ومتصل أثناء فتح الطرد المغلف فور استلامه. في حال وجود أي تلف ناتج عن الشحن، يُعد هذا الفيديو دليلاً أساسياً على مسؤولية شركة الشحن.",
        returnsBullet2Strong: "مهلة الاستبدال والاسترجاع (14 يوماً):",
        returnsBullet2Text: "يُسمح بالاستبدال أو الاسترجاع خلال 14 يوماً من الاستلام بشرط أن يكون المنتج بحالته الأصلية وغير مستخدم وبكامل تغليفه الأصلي.",
        returnsBullet3Strong: "المنتجات غير القابلة للاسترجاع:",
        returnsBullet3Text: "اللوحات الفنية (Paintings)، والطلبات الخاصة (Special Orders)، وأي منتج ليس بحالته الأصلية لا يمكن استرجاعها أو استبدالها.",
        viewFullPolicies: "عرض كافة السياسات والشروط ←",
        customerReviews: "تقييمات العملاء",
        noReviewsYet: "لا توجد تقييمات بعد. كن أول من يقيّم هذا المنتج!",
        writeReview: "أضف تقييمك",
        ratingLabel: "التقييم",
        reviewLabel: "رأيك في المنتج",
        reviewPlaceholder: "شاركنا تجربتك ورأيك في هذا المنتج...",
        submitting: "جاري الإرسال...",
        submitReview: "إرسال التقييم",
        verifiedBuyerNotice: "التقييم متاح فقط للعملاء الذين قاموا بشراء واستلام هذا المنتج.",
        loginToReview: "يرجى تسجيل الدخول لإضافة تقييم.",

        // SearchOverlay
        searchPlaceholder: "ابحث عن المنتجات...",
        noSearchResults: 'لا توجد نتائج مطابقة لـ "{query}"',

        // Cart
        shoppingCart: "سلة التسوق",
        cartEmpty: "سلة التسوق فارغة.",
        continueShopping: "مواصلة التسوق",
        remove: "حذف",
        subtotal: "المجموع الفرعي",
        shippingCalculatedAtCheckout: "يتم حساب تكلفة الشحن عند إتمام الطلب.",
        proceedToCheckout: "إتمام الشراء",

        // CheckoutModal
        checkoutTitle: "إتمام الطلب",
        shippingInfoHeading: "1. بيانات الشحن والتوصيل",
        fullNamePlaceholder: "الاسم بالكامل *",
        phonePlaceholder: "رقم الهاتف *",
        emailInvoicePlaceholder: "البريد الإلكتروني (لإرسال رقم الطلب والفاتورة) *",
        selectCityPlaceholder: "اختر المحافظة / المدينة *",
        streetAddressPlaceholder: "العنوان بالتفصيل (الشارع، رقم العقار، الدور، الشقة) *",
        couponPlaceholder: "كود الخصم",
        iHaveReadAndAgree: "لقد قرأت وأوافق على",
        termsAndConditions: "الشروط والأحكام",
        returnAndExchangePolicy: "سياسة الاستبدال والاسترجاع",
        noCourierInspectionNote: "(بما في ذلك عدم فتح الشحنة قبل الاستلام من المندوب وأهمية تصوير فيديو فتح الشحنة)",
        andText: "و",
        buyingAndShippingPolicy: "سياسة الشراء والشحن",
        paymentMethodHeading: "2. طريقة الدفع",
        creditDebitCard: "بطاقة بنكية (Credit / Debit Card)",
        paySecurelyVisaMastercard: "ادفع بأمان عبر فيزا، ماستركارد، أمريكان إكسبريس، أو ميزة",
        cashOnDelivery: "الدفع عند الاستلام (COD)",
        payCashUponReceiving: "ادفع نقداً للمندوب عند استلام طلبك",
        loadingCardFields: "جاري تحميل نموذج الدفع الآمن...",
        fillShippingFirstTitle: "يرجى إدخال بيانات الشحن بالأعلى أولاً",
        fillShippingFirstSub: "بمجرد إدخال الاسم ورقم الهاتف والبريد الإلكتروني والمدينة والعنوان، سيتم تفعيل خانات البطاقة البنكية تلقائياً.",
        agreeTermsForCardTitle: "يرجى الموافقة على سياسات وشروط المتجر للمتابعة بالدفع بالبطاقة",
        iAgreeToTermsBtn: "أوافق على الشروط والأحكام",
        shippingLabel: "الشحن",
        freeLabel: "مجاني",
        paymentFeeLabel: "رسوم الدفع",
        discountLabel: "الخصم",
        totalLabel: "الإجمالي",
        checkTermsBeforeOrderWarning: "يرجى تحديد مربع الموافقة على الشروط والأحكام بالأعلى قبل إتمام الطلب.",
        placeOrderCodBtn: "تأكيد الطلب (الدفع عند الاستلام)",
        waitSignUpSave: "انتظر! هل ترغب في إنشاء حساب؟",
        createAccountPromo: "أنشئ حساباً الآن للحصول على عروض حصرية ومتابعة طلباتك بسهولة.",
        signUpLoginBtn: "إنشاء حساب / تسجيل الدخول",
        orContinueAsGuest: "أو المتابعة كزائر:",
        enterEmailForInvoice: "أدخل بريدك الإلكتروني لإرسال الفاتورة",
        continueAsGuestBtn: "المتابعة كزائر",

        // OrderConfirmationModal
        orderPlacedTitle: "تم تأكيد طلبك بنجاح!",
        thankYouForShopping: "شكراً لتسوقك من Ukiyo Styling Homes.",
        checkEmailForInvoice: "يرجى مراجعة بريدك الإلكتروني للاطلاع على رقم الطلب وتفاصيل الفاتورة.",

        // Footer
        quickLinks: "روابط سريعة",
        findUsHere: "تواصل معنا",
        storeAddress: "19 شارع فريد بك، كفر عبده، الإسكندرية.",
        whatsappLabel: "واتساب",
        weAccept: "طرق الدفع المتاحة",
        allRightsReserved: "Ukiyo. جميع الحقوق محفوظة.",

        // Policies & Consent Banner / Modal / Page
        storePoliciesBannerTitle: "سياسات متجر Ukiyo:",
        pleaseReviewAndAccept: "يرجى مراجعة والموافقة على",
        decline: "رفض",
        acceptAll: "موافقة على الكل",
        storePoliciesAndTerms: "سياسات وشروط المتجر",
        policiesAndTermsTitle: "السياسات والشروط والأحكام",
        policiesPageSubtitle: "اختر القسم أدناه لقراءة سياسة الاستبدال والاسترجاع، وسياسة الشراء والشحن، والشروط والأحكام.",
        backToHome: "العودة للرئيسية",
        close: "إغلاق",
        iAcceptAndAgree: "أوافق على الشروط",

        // Auth, Profile, Orders, Wishlist
        createAccount: "إنشاء حساب جديد",
        loginToUkiyo: "تسجيل الدخول إلى Ukiyo",
        fullName: "الاسم بالكامل",
        email: "البريد الإلكتروني",
        password: "كلمة المرور",
        register: "إنشاء حساب",
        login: "تسجيل الدخول",
        orDivider: "أو",
        signInWithGoogle: "تسجيل الدخول عبر Google",
        alreadyHaveAccount: "لديك حساب بالفعل؟ تسجيل الدخول",
        dontHaveAccount: "ليس لديك حساب؟ إنشاء حساب",
        useSignInLink: "الدخول عبر رابط البريد الإلكتروني",
        signInWithEmailLink: "الدخول عبر رابط البريد الإلكتروني",
        enterEmailForLink: "أدخل بريدك الإلكتروني لاستلام رابط دخول سريع بدون كلمة مرور.",
        sendLink: "إرسال الرابط",
        backToPasswordLogin: "العودة لتسجيل الدخول بكلمة المرور",
        checkYourEmail: "تحقق من بريدك الإلكتروني",
        manageShippingAndAccount: "إدارة بيانات الشحن وإعدادات حسابك.",
        city: "المدينة / المحافظة",
        selectACity: "اختر المدينة",
        detailedAddress: "العنوان بالتفصيل",
        saveChanges: "حفظ التغييرات",
        accountSettings: "إعدادات الحساب",
        emailAddress: "البريد الإلكتروني",
        cancel: "إلغاء",
        changeEmail: "تغيير البريد الإلكتروني",
        newEmail: "البريد الإلكتروني الجديد",
        currentPasswordVerify: "كلمة المرور الحالية (للتأكيد)",
        updateEmail: "تحديث البريد الإلكتروني",
        resetPassword: "إعادة تعيين كلمة المرور",
        loadingOrders: "جاري تحميل طلباتك...",
        pleaseLoginOrders: "يرجى تسجيل الدخول لعرض طلباتك.",
        orderNumber: "طلب رقم #{id}",
        placedOn: "تاريخ الطلب:",
        justNow: "الآن",
        noOrdersYet: "لم تقم بإجراء أي طلبات حتى الآن.",
        wishlistEmpty: "قائمة المفضلة فارغة.",
        startExploring: "ابدأ بتصفح المنتجات"
    }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState(() => {
        try {
            const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
            return saved === 'ar' ? 'ar' : 'en';
        } catch {
            return 'en';
        }
    });

    const isRTL = lang === 'ar';

    useEffect(() => {
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        } catch {}
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }, [lang]);

    const setLang = useCallback((newLang) => {
        setLangState(newLang === 'ar' ? 'ar' : 'en');
    }, []);

    const toggleLanguage = useCallback(() => {
        setLangState(prev => (prev === 'ar' ? 'en' : 'ar'));
    }, []);

    const t = useCallback((key, vars = {}) => {
        const dict = translations[lang] || translations.en;
        let str = dict[key] !== undefined ? dict[key] : (translations.en[key] !== undefined ? translations.en[key] : key);
        if (vars && typeof str === 'string') {
            Object.entries(vars).forEach(([k, v]) => {
                str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
            });
        }
        return str;
    }, [lang]);

    const getProductName = useCallback((product) => {
        if (!product) return '';
        if (lang === 'ar' && product.nameAr && String(product.nameAr).trim() !== '') {
            return String(product.nameAr).trim();
        }
        return product.name || '';
    }, [lang]);

    const getProductDescription = useCallback((product) => {
        if (!product) return '';
        if (lang === 'ar' && product.descriptionAr && String(product.descriptionAr).trim() !== '') {
            return String(product.descriptionAr).trim();
        }
        return product.description || '';
    }, [lang]);

    const getCategoryLabel = useCallback((category) => {
        if (!category) return '';
        if (lang === 'ar') {
            const lower = String(category).trim().toLowerCase();
            if (CATEGORY_AR_MAP[lower]) {
                return CATEGORY_AR_MAP[lower];
            }
        }
        return category;
    }, [lang]);

    const getCityLabel = useCallback((city) => {
        if (!city) return '';
        if (lang === 'ar' && CITY_AR_MAP[city]) {
            return CITY_AR_MAP[city];
        }
        return city;
    }, [lang]);

    const value = useMemo(() => ({
        lang,
        isRTL,
        setLang,
        toggleLanguage,
        t,
        getProductName,
        getProductDescription,
        getCategoryLabel,
        getCityLabel
    }), [lang, isRTL, setLang, toggleLanguage, t, getProductName, getProductDescription, getCategoryLabel, getCityLabel]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
